import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { IconCheck, IconChevronDown, IconClose, IconSearch } from '../icons';
import FormField, { FormFieldProps } from './FormField';
import { textInputIconStyles, textInputStyles } from './TextInput';

export interface OptionType {
  value: string;
  label: string;
}

interface MultiSelectSearchFieldProps extends Omit<FormFieldProps, 'children'> {
  options: OptionType[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xs';
  hasError?: boolean;
}

export const MultiSelectSearchField: React.FC<MultiSelectSearchFieldProps> = ({
  options,
  value: selectedValues,
  onChange,
  placeholder = 'Select options',
  searchPlaceholder = 'Search...',
  label,
  hint,
  error,
  id,
  className,
  disabled,
  size = 'sm',
  hasError = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelectOption = (optionValue: string) => {
    const newSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newSelectedValues);
  };

  const handleRemoveOption = (optionValue: string) => {
    onChange(selectedValues.filter((v) => v !== optionValue));
  };

  return (
    <FormField
      label={label}
      hint={hint}
      error={error}
      id={id}
      className={className}
      disabled={disabled}
    >
      <div className="relative" ref={wrapperRef}>
        <button
          tabIndex={0}
          className={twMerge(
            textInputStyles({ size, hasError }),
            'relative flex min-h-[auto] cursor-pointer flex-wrap items-center gap-1 overflow-hidden',
            'bg-neutral-white px-3 py-2 text-body-sm-desktop text-neutral-black',
            'h-auto data-[placeholder]:text-neutral-500',
            clsx({
              'pointer-events-none bg-neutral-950': disabled,
            }),
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
        >
          {selectedValues.length === 0 && (
            <span
              className={`typography-body-sm ${isOpen ? 'text-neutral-black' : 'text-neutral-500'}`}
            >
              {placeholder}
            </span>
          )}
          {selectedValues.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return (
              <span
                key={val}
                className="flex items-center gap-1 rounded bg-neutral-950 py-[5px] pl-2 pr-1 text-body-sm-desktop text-neutral-black"
              >
                {option?.label || val}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveOption(val);
                    }}
                    className="ml-1 p-1.5 text-neutral-500 hover:text-neutral-800 focus:outline-none"
                  >
                    <IconClose className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            );
          })}
          {isOpen && (
            <input
              type="text"
              value=""
              readOnly
              className="flex-grow bg-transparent p-0 outline-none"
            />
          )}
          {!disabled && (
            <IconChevronDown
              className={textInputIconStyles({
                position: 'sufix',
                size,
                className: 'pointer-events-none',
              })}
            />
          )}
        </button>

        {isOpen && !disabled && (
          <div className="absolute z-dropdown mt-1 max-h-40 w-full overflow-y-auto rounded-md border border-neutral-800 bg-neutral-white p-1 shadow-lg">
            {filteredOptions.length > 0 && (
              <div className="relative mb-1 flex items-center">
                <IconSearch className="absolute left-3 h-4 w-4 text-neutral-500" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-sm w-full rounded-md border border-neutral-800 bg-neutral-white py-2 pl-9 pr-3 text-neutral-black outline-none"
                />
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="option"
                  tabIndex={0}
                  aria-selected={selectedValues.includes(option.value)}
                  onClick={() => handleSelectOption(option.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectOption(option.value);
                    }
                  }}
                  className={twMerge(
                    'group relative flex w-full cursor-pointer select-none items-center justify-between rounded-md px-2.5 py-1.5 text-left outline-none typography-body-sm data-[disabled]:pointer-events-none',
                    selectedValues.includes(option.value)
                      ? 'bg-secondary-800'
                      : 'hover:bg-secondary-800',
                  )}
                >
                  <span>{option.label}</span>
                  <div className="flex h-5 w-5 items-center justify-center rounded-sm border border-neutral-300">
                    {selectedValues.includes(option.value) && (
                      <IconCheck className="h-4 w-4 text-neutral-black" />
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="text-sm flex justify-center px-2 py-1.5 text-neutral-500">
                No options found.
              </div>
            )}
          </div>
        )}
      </div>
    </FormField>
  );
};
